import { Component, OnInit, OnDestroy, Inject, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { CalendarService } from 'src/app/services/calendar.service';
import { Router } from '@angular/router';
import { PortalEvent } from 'src/app/classes/calendar';
import { Sort } from '@angular/material/sort';
import { MediaMatcher } from '@angular/cdk/layout';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { CalendarTypeService } from 'src/app/services/calendar-type.service';
import { CalendarEvent } from 'angular-calendar';
import { FormControl } from '@angular/forms';


@Component({
	selector: 'app-event-list',
	templateUrl: './event-list.component.html',
	styleUrls: [ './event-list.component.css' ],
})
export class EventListComponent implements OnInit, OnDestroy {


	bLoading = true;
	subscriptions = new Subscription();

	viewDate = new Date();
	events: PortalEvent[] = [];
	filteredEvents: PortalEvent[] = [];

	nbCharMax = 30;
	smQuery = this.media.matchMedia('(max-width: 450px)');
	mQuery = this.media.matchMedia('(max-width: 959px)');
	private _queryListener = () => {};

	bAdmin = this.authenticationService.isAdmin();
	
	@Input() isInternalCalendar: boolean = false;

	filterCtrl = new FormControl<"past"|"future"|""|null>( "future" );


	constructor(
		private calendarService: CalendarService,
		private router: Router,
		private media: MediaMatcher,
		private authenticationService: AuthenticationService,
		private calendarTypeService: CalendarTypeService,
		@Inject('LOCALSTORAGE') private localStorage: Storage
	) {}


	ngOnInit() {

		this.subscriptions.add(
			this.filterCtrl.valueChanges.subscribe( filter => {
				this.filterEvents( filter );
			})
		);

		this.subscriptions.add(
			this.calendarTypeService.isInternalCalendar$.subscribe(isInternal => {
			  const storedType = this.localStorage.getItem('calendarType');
			  const newType = isInternal ? 'internal' : 'external';
			  
			  if (storedType !== newType) {
				this.isInternalCalendar = isInternal;
				this.localStorage.setItem('calendarType', newType);
				this.loadEvents();  // Events laden, wenn sich der Kalendertyp ändert
			  }
			})
		  );

		  this.loadEvents();

		this._queryListener = () => {
			if( this.smQuery.matches ) this.nbCharMax = 35;
			else if( this.mQuery.matches ) this.nbCharMax = 80;
			else this.nbCharMax = 90;
		}
		this.smQuery.addEventListener( 'change', this._queryListener );
		this.mQuery.addEventListener( 'change', this._queryListener );
		this._queryListener();

		this.filterCtrl.patchValue( "future", { emitEvent: true } );

	}

	ngOnDestroy() {
		
		this.subscriptions.unsubscribe();

	}


	private filterEvents( filter: "past"|"future"|""|null ) {

		if( !filter ) {
			this.filteredEvents = [ ...this.events ];
		}
		else {
			const dt = new Date();
			const tmsNow = dt.getTime();
			if( filter == "past" ) {
				this.filteredEvents = this.events.filter( e => e.tmsStart && (e.tmsStart <= tmsNow) );
			}
			else {
				this.filteredEvents = this.events.filter( e => e.tmsStart && (e.tmsStart > tmsNow) );
			}
		}

		this.sortList( "asc", "startDate" );

	}


	eventClicked(event: PortalEvent) {

		this.calendarTypeService.setInternalCalendar(event.nurIntern);

		const url = this.bAdmin ? "bearbeiten" : "visualisieren";
		this.router.navigate(["Veranstaltungen/" + url], { queryParams: { id: event.id } });

	}


	sortData( sort: Sort ) {

		if( !sort.active || sort.direction === '' ) {
			return;
		}

		this.sortList( sort.direction, sort.active );

	}

	private sortList( direction: string, property: string ) {

		this.filteredEvents = [ ...this.filteredEvents ].sort( (a, b) => {

			let result = 0;
			switch( property ) {
				case 'eventName':
					result = a.name.localeCompare( b.name );
					break;
				case 'startDate':
					if(  ( a.tmsStart === null ) && ( b.tmsStart === null )  ) result = 0;
					else if( a.tmsStart === null ) result = -1;
					else if( b.tmsStart === null ) result = 1;
					else result = a.tmsStart - b.tmsStart;
					break;
				case 'bReleased':
					if( a.bReleased === b.bReleased ) result = 0;
					else if( a.bReleased === true ) result = 1;
					else if( b.bReleased === true ) result = -1;
					break;
					case 'nurIntern':
                result = a.nurIntern === b.nurIntern ? 0 : a.nurIntern ? -1 : 1;
                break;
        
			}

			const isAsc = ( direction === 'asc' ) ? 1 : -1;
			return result * isAsc;

		});

	}

	private loadEvents() {

		this.bLoading = true;
		this.calendarService.getCalendarEvents(this.isInternalCalendar).subscribe(events => {

			this.bLoading = false;

			// Konvertiere CalendarEvent[] zu PortalEvent[]
			this.events = events.map(e => this.convertToPortalEvent(e));
			
			// Sortiere die Events nach Startdatum
			this.events.sort((a, b) => {
				if ((a.tmsStart === null) && (b.tmsStart === null)) return 0;
				if (a.tmsStart === null) return -1;
				if (b.tmsStart === null) return 1;
				return a.tmsStart - b.tmsStart;
			});

			this.filteredEvents = [ ...this.events ];

			this.filterEvents( this.filterCtrl.value );

		});
	}
	  
	
	// Konvertiere CalendarEvent zu PortalEvent
	private convertToPortalEvent(event: CalendarEvent<any>): PortalEvent {
		const portalEvent = new PortalEvent();

		if (event.id !== undefined && event.id !== null) {
			portalEvent.id = event.id.toString();
		  } else {
			console.warn('Event ID ist nicht definiert:', event);
		  }		

		portalEvent.name = event.title;
		portalEvent.tmsStart = event.start.getTime(); // Beispielkonvertierung
		portalEvent.tmsEnd = event.end ? event.end.getTime() : null;
		portalEvent.bReleased = event.meta?.bReleased || false;
		portalEvent.nurIntern = event.meta?.nurIntern || false;
		// Füge weitere Felder nach Bedarf hinzu
	
		return portalEvent;
	}
}

